import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { useGeoStore } from '../store/geoStore';
import { trackEvent, flushEvents } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';

const DISMISS_KEY = 'x1493_geo_notice_dismissed';
const SHOWN_KEY = 'x1493_geo_notice_shown';
const MAX_SHOWS_PER_SESSION = 1;
const DELAY_MS = 2000;

const DEFAULT_NOTICE =
  '偵測到您目前位於海外，冷凍及部分商品無法出境，最終可購買商品以收貨地址為準。';

// 海外訪客溫馨提示（客人 2026-09-05 業務規則第一條）：
// 僅營銷提示——不限制瀏覽、不影響購買權限（權限以收貨地址為準）。
// 顯示節奏沿用 ProductRecommendPopup 慣例：延遲彈出、每 session 限次、「不再提醒」永久記住。
export default function GeoNoticePopup() {
  const visitorRegion = useGeoStore((s) => s.visitorRegion);
  const { getValue } = useCMS();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (visitorRegion !== 'overseas') return;
    if (window.localStorage.getItem(DISMISS_KEY) === 'true') return;

    const shown = Number(window.sessionStorage.getItem(SHOWN_KEY) || '0');
    if (shown >= MAX_SHOWS_PER_SESSION) return;

    const timer = setTimeout(() => {
      setVisible(true);
      window.sessionStorage.setItem(SHOWN_KEY, String(shown + 1));
      trackEvent('geo_notice_impression', { region: 'overseas', pagePath: location.pathname });
      flushEvents();
    }, DELAY_MS);

    return () => clearTimeout(timer);
  }, [visitorRegion, location.pathname]);

  // 進場動畫
  useEffect(() => {
    if (!visible) return undefined;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Esc 關閉
  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleClose = () => {
    if (dontShowAgain && typeof window !== 'undefined') {
      window.localStorage.setItem(DISMISS_KEY, 'true');
    }
    trackEvent('geo_notice_close', { region: 'overseas', pagePath: location.pathname });
    flushEvents();
    setEntered(false);
    setTimeout(() => setVisible(false), 250);
  };

  if (!visible) return null;

  const notice = getValue('geo.notice.overseas', DEFAULT_NOTICE);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="geo-notice-title"
      className="fixed inset-0 z-[998] flex items-center justify-center p-4"
    >
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-250 ${entered ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      <div
        className={`relative w-full max-w-[400px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transition-all duration-250 ease-out border border-gray-200 dark:border-gray-700 ${
          entered ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'
        }`}
      >
        <button
          onClick={handleClose}
          aria-label="關閉"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/10 hover:bg-black/25 text-gray-600 dark:text-gray-300 flex items-center justify-center text-lg leading-none transition"
        >
          ×
        </button>

        <div className="px-6 pt-8 pb-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <Globe size={28} className="text-amber-600 dark:text-amber-400" />
          </div>
          <h3
            id="geo-notice-title"
            className="text-lg font-bold text-gray-900 dark:text-white mb-3"
          >
            🌏 海外顧客溫馨提示
          </h3>
          <p className="text-[14px] text-gray-700 dark:text-gray-300 leading-relaxed mb-6 text-left">
            {notice}
          </p>

          <Link
            to="/store"
            onClick={handleClose}
            className="block w-full bg-x1493-primary hover:bg-red-700 text-white font-bold text-sm rounded-full py-3 transition mb-3"
          >
            瀏覽可出口商品 →
          </Link>

          <label className="flex items-center justify-center gap-2 text-[11.5px] text-gray-500 dark:text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-3.5 h-3.5 accent-x1493-primary"
            />
            不再提醒我
          </label>
        </div>
      </div>
    </div>
  );
}
